import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "./app/ErrorBoundary.js";
import { ThemeProvider } from "./app/ThemeProvider.js";
import { AuthProvider } from "./app/AuthProvider.js";
import { ActiveScopeProvider } from "./app/providers/ActiveScopeProvider.js";
import { ScopedQueryProvider } from "./app/ScopedQueryProvider.js";
import { AppRouter } from "./app/Router.js";
import { createDevAuthAdapter } from "./adapters/auth/DevAuthAdapter.js";
import { AzimAuthAdapter } from "./adapters/auth/AzimAuthAdapter.js";
import type { AuthPort } from "./adapters/auth/AuthPort.js";
import { setTokenRefresher } from "./services/ApiClient.js";
import { initWebVitalsReporter } from "./adapters/telemetry/webVitalsReporter.js";
import { createErrorReporter } from "./adapters/telemetry/errorReporter.js";
import "./styles/global.css";

// Telemetria: Web Vitals + sink de erros de componentes (DD-010, RNF 3, RNF 10.2).
// No-op silencioso se VITE_VITALS_ENDPOINT / VITE_ERROR_ENDPOINT não estiverem configurados.
initWebVitalsReporter();
const reportError = createErrorReporter();

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Elemento #root não encontrado no DOM.");

const isProd = Boolean(import.meta.env["PROD"]);
const isDev = Boolean(import.meta.env["DEV"]);
const apiBaseUrl: string = import.meta.env["VITE_API_BASE_URL"] ?? "/v1";

// Determina se a área `session` está promovida para a API real.
// Em produção todas as áreas são sempre reais (isAreaReal retorna true).
// Em dev, verifica se VITE_API_REAL_AREAS contém "session" ou "*".
const rawRealAreas = (import.meta.env["VITE_API_REAL_AREAS"] ?? "") as string;
const sessionAreaIsReal =
  isProd ||
  rawRealAreas.trim() === "*" ||
  rawRealAreas
    .split(",")
    .map((s) => s.trim())
    .includes("session");

// Adapter do IdP self-hosted (authentication service via gateway YARP).
// Ativado em produção E em dev quando `session` está promovida para API real
// (VITE_API_REAL_AREAS inclui "session" ou "*"), permitindo validar o caminho
// real em ambiente local sem precisar de build de produção.
// Slug do tenant para o header X-Tenant-Slug do IdP self-hosted (ADR-0017).
const tenantSlug: string = import.meta.env["VITE_AUTH_TENANT_SLUG"] ?? "azim";
const azimAuthAdapter: AzimAuthAdapter | null = sessionAreaIsReal
  ? new AzimAuthAdapter({ baseUrl: apiBaseUrl, tenantSlug })
  : null;

// Refresh-on-401: o ApiClient renova o access token (POST /auth/token/refresh)
// e reenvia a requisição UMA vez ao receber 401. Sem isto, após o token expirar
// (~horas) as chamadas falham silenciosamente enquanto o cache do react-query
// mascara dados antigos (causa-raiz dos 401 intermitentes em /auth/me, /inbox).
if (azimAuthAdapter) {
  setTokenRefresher(() => azimAuthAdapter.getIdToken(true));
}

// Ponte de token DEV (fake-jwks, ADR-0013): fallback exclusivo para dev quando
// `session` NÃO está promovida. Em produção, createDevAuthAdapter retorna null
// (guard por env — sem bypass em prod). Nula quando azimAuthAdapter já está ativo.
const devAuthAdapter = !azimAuthAdapter
  ? createDevAuthAdapter({
      isDev,
      isProd,
      tokenUrl: import.meta.env["VITE_DEV_AUTH_TOKEN_URL"],
    })
  : null;

// Adapter efetivo injetado no AuthProvider:
//   AzimAuthAdapter (prod / session promovida)
//   → DevAuthAdapter  (dev + fake-jwks configurado)
//   → devAuthStub interno do AuthProvider (dev sem qualquer adapter configurado)
const effectiveAuthAdapter: AuthPort | undefined =
  azimAuthAdapter ?? devAuthAdapter ?? undefined;

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary onReportError={reportError}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider
            {...(effectiveAuthAdapter ? { authAdapter: effectiveAuthAdapter } : {})}
          >
            <a href="#az-main" className="skip-link">
              Pular para o conteúdo
            </a>
            <ActiveScopeProvider>
              <ScopedQueryProvider>
                <AppRouter />
              </ScopedQueryProvider>
            </ActiveScopeProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
